import { Suspense, lazy, useCallback, useState } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { MobileSidebarProvider } from './contexts/MobileSidebarContext';
import { LandingPage } from './components/Landing/LandingPage';
import { AuthPage } from './components/Auth/AuthPage';
import { AuthConfirmPage } from './components/Auth/AuthConfirmPage';
import { ResetPasswordPage } from './components/Auth/ResetPasswordPage';
import { AppLayout } from './components/Layout/AppLayout';
import { GlobalMobileSidebar } from './components/Layout/GlobalMobileSidebar';
import { AppRouteGuard } from './components/AppRouteGuard';
import { OnboardingRouter } from './components/Onboarding/OnboardingRouter';
import NotFound from './pages/NotFound';
import { Provider as JotaiProvider } from 'jotai';
import { ScrollToTop } from './components/UI/ScrollToTop';
import { usePageTracking } from './hooks/usePageTracking';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastContainer } from './components/UI/Toast';
import type { ToastType } from './components/UI/Toast';
import { StagingJobCompletionToaster, type ToastPayload } from './components/UI/StagingJobCompletionToaster';

// Lazy-loaded app pages (internal/protected)
const Dashboard = lazy(() => import('./pages/app/Dashboard'));
const ProfilePage = lazy(() => import('./pages/app/Profile'));
const ProjectView = lazy(() => import('./pages/app/ProjectView'));

// Lazy-loaded marketing pages
const VirtualStagingPillar = lazy(() => import('./pages/marketing/VirtualStagingPillar'));
const AudienceAgents = lazy(() => import('./pages/marketing/AudienceAgents'));
const AudiencePhotographers = lazy(() => import('./pages/marketing/AudiencePhotographers'));
const AudienceBrokers = lazy(() => import('./pages/marketing/AudienceBrokers'));
const StylesHub = lazy(() => import('./pages/marketing/StylesHub'));
const StyleDetail = lazy(() => import('./pages/marketing/StyleDetail'));
const Pricing = lazy(() => import('./pages/marketing/Pricing'));
const BeforeAfter = lazy(() => import('./pages/marketing/BeforeAfter'));
const FaqHub = lazy(() => import('./pages/marketing/FaqHub'));
const InteriorAiAlternative = lazy(() => import('./pages/marketing/InteriorAiAlternative'));
const PartnersPhotographers = lazy(() => import('./pages/marketing/PartnersPhotographers'));
const PartnersBrokerages = lazy(() => import('./pages/marketing/PartnersBrokerages'));
const PrivacyPolicy = lazy(() => import('./pages/marketing/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/marketing/TermsOfService'));

const StyleLibrary = lazy(() => import('./pages/app/StyleLibrary'));
const Editor = lazy(() => import('./pages/app/Editor'));

function AppContent() {
  const { user, loading, isRecoverySession } = useAuth();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<Array<{ id: string; type?: ToastType } & ToastPayload>>([]);

  const showToast = useCallback((payload: ToastPayload) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => [...prev, { id, ...payload }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Track page views on route changes
  usePageTracking();

  // User is considered logged in only if they have a user AND it's not a recovery session
  const isLoggedIn = user !== null && !isRecoverySession;

  const handleGetStarted = () => {
    navigate('/auth?mode=signup');
  };

  const Loading = (
    <div className="min-h-screen bg-alabaster flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-olive border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-sand font-sans">Loading...</p>
      </div>
    </div>
  );

  return (
    <Suspense fallback={Loading}>
      <ScrollToTop />
      <StagingJobCompletionToaster onToast={showToast} />
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <Routes>
        {/* Marketing routes (public) */}
        <Route path="/virtual-staging" element={<VirtualStagingPillar />} />
        <Route path="/virtual-staging/real-estate-agents" element={<AudienceAgents />} />
        <Route path="/virtual-staging/photographers" element={<AudiencePhotographers />} />
        <Route path="/virtual-staging/broker-teams" element={<AudienceBrokers />} />
        <Route path="/virtual-staging/styles" element={<StylesHub />} />
        <Route path="/virtual-staging/styles/:style" element={<StyleDetail />} />
        <Route path="/virtual-staging/pricing" element={<Pricing />} />
        <Route path="/virtual-staging/before-after" element={<BeforeAfter />} />
        <Route path="/virtual-staging/faq" element={<FaqHub />} />
        <Route
          path="/virtual-staging/interior-ai-alternative"
          element={<InteriorAiAlternative />}
        />
        <Route path="/partners/photographers" element={<PartnersPhotographers />} />
        <Route path="/partners/brokerages" element={<PartnersBrokerages />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/confirm" element={<AuthConfirmPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Landing/Home route */}
        <Route
          path="/"
          element={
            loading ? (
              Loading
            ) : !isLoggedIn ? (
              <LandingPage onGetStarted={handleGetStarted} />
            ) : (
              <Navigate to="/dashboard" replace />
            )
          }
        />

        {/* Onboarding routes (protected, but no AppLayout - standalone experience) */}
        <Route
          path="/onboarding/*"
          element={
            loading ? (
              Loading
            ) : !isLoggedIn ? (
              <Navigate to="/auth" replace />
            ) : (
              <OnboardingRouter />
            )
          }
        />

        {/* Protected routes with AppLayout wrapper (standard pages with sidebar) */}
        {/* Wrapped with AppRouteGuard to handle pending_subscription and onboarding checks */}
        <Route
          element={
            loading ? (
              Loading
            ) : !isLoggedIn ? (
              <Navigate to="/auth" replace />
            ) : (
              <AppRouteGuard>
                <AppLayout />
              </AppRouteGuard>
            )
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/styles" element={<StyleLibrary />} />
          <Route path="/project/:id" element={<ProjectView />} />
        </Route>

        {/* Editor - full-screen immersive experience with floating UI */}
        {/* Also wrapped with AppRouteGuard to respect pending_subscription and onboarding */}
        <Route
          path="/editor"
          element={
            loading ? (
              Loading
            ) : !isLoggedIn ? (
              <Navigate to="/auth" replace />
            ) : (
              <AppRouteGuard>
                <Editor />
              </AppRouteGuard>
            )
          }
        />

        {/* 404 - Catch all unmatched routes (must be last) */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <JotaiProvider>
        <AuthProvider>
          <AnalyticsProvider>
            <MobileSidebarProvider>
              <AppContent />
              {/* Global mobile sidebar rendered at root level for access across all pages */}
              <GlobalMobileSidebar />
            </MobileSidebarProvider>
          </AnalyticsProvider>
        </AuthProvider>
      </JotaiProvider>
    </ErrorBoundary>
  );
}

export default App;
